import {
  useState,
  useMemo,
} from 'react';
import {
  FileText,
  DollarSign,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useInvoices,
  InvoiceCard,
  InvoiceDetailModal,
} from '@/features/finance';
import type {
  Invoice, InvoiceStatus 
} from '@/features/finance';
import { toast } from 'sonner';

const STATUS_TABS: {
  value: InvoiceStatus | undefined;
  label: string;
}[] = [
  {
    value: undefined,
    label: 'All',
  },
  {
    value: 'pending',
    label: 'Pending',
  },
  {
    value: 'matched',
    label: 'Matched',
  },
  {
    value: 'disputed',
    label: 'Disputed',
  },
  {
    value: 'approved',
    label: 'Approved',
  },
  {
    value: 'paid',
    label: 'Paid',
  },
];

export function InvoicesView() {
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus | undefined>(undefined);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useInvoices({ status: selectedStatus });
  const { data: allData } = useInvoices();

  const invoices = data?.data ?? [];

  const stats = useMemo(() => {
    const invs = allData?.data ?? [];
    return {
      total: invs.length,
      pending: invs.filter((i) => i.status === 'pending').length,
      disputed: invs.filter((i) => i.status === 'disputed').length,
      pendingAmount: allData?.meta?.pendingAmount ?? 0,
    };
  }, [allData]);

  const exportData = invoices.map((inv) => ({
    invoiceNumber: inv.invoiceNumber,
    supplier: inv.supplierName,
    status: inv.status,
    subtotal: inv.subtotal,
    total: inv.total,
    currency: inv.currency,
    dueDate: inv.dueDate,
    poNumber: inv.poNumber,
  }));

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported 10 invoices');
      refetch();
    }, 1500);
  };

  const handleApproveInvoice = (invoice: Invoice) => {
    toast.success(`Invoice ${invoice.invoiceNumber} approved`);
    setSelectedInvoice(null);
    refetch();
  };

  const handleDisputeInvoice = (invoice: Invoice) => {
    toast.info(`Invoice ${invoice.invoiceNumber} marked as disputed`);
    setSelectedInvoice(null);
    refetch();
  };

  const handleMarkPaid = (invoice: Invoice) => {
    toast.success(`Invoice ${invoice.invoiceNumber} marked as paid`);
    setSelectedInvoice(null);
    refetch();
  };

  const handleViewPO = (invoice: Invoice) => {
    toast.info(`Viewing PO ${invoice.poNumber}`);
  };

  return (
    <>
      <CrudToolbar
        entityName="Invoice"
        entityNamePlural="Invoices"
        selectedCount={0}
        onBulkUpload={handleBulkUpload}
        exportData={exportData}
        exportFilename="invoices.csv"
        canAdd={false}
        canEdit={false}
        canDelete={false}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Invoices
            </CardTitle>
            <FileText className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Review
            </CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Disputed</CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.disputed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Amount
            </CardTitle>
            <DollarSign className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${stats.pendingAmount.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={selectedStatus === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={`skel-inv-${i.toString()}`}
              className="h-64 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load invoices</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && invoices.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">No invoices found</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && invoices.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {invoices.map((invoice) => (
            <div
              key={invoice.invoiceId}
              className="cursor-pointer"
              onClick={() => setSelectedInvoice(invoice)}
            >
              <InvoiceCard invoice={invoice} />
            </div>
          ))}
        </div>
      )}

      <InvoiceDetailModal
        invoice={selectedInvoice}
        isOpen={selectedInvoice !== null}
        onClose={() => setSelectedInvoice(null)}
        onApprove={handleApproveInvoice}
        onDispute={handleDisputeInvoice}
        onMarkPaid={handleMarkPaid}
        onViewPO={handleViewPO}
      />
    </>
  );
}
